/**
 * Workflows Service
 * Main service for managing workflows in Supabase
 *
 * PART 9: EXECUTION ENGINE TODOs
 * =============================
 *
 * New service: workflow-executor.service.ts should contain:
 * - executeWorkflow(workflowId, canvasDefinition): Main execution orchestrator
 * - validateWorkflow(definition): Validate all nodes, edges, config before execution
 * - compileCanvasToDefinition(canvasNodes, canvasEdges): Convert visual canvas to executable format
 * - executeNode(nodeId, nodeData, executionContext): Execute single node/action
 * - evaluateCondition(conditionNode, context): Evaluate true/false for branching
 * - initializeTriggerListeners(): Start listening to trigger events (on_change, scheduled, webhooks)
 * - stopWorkflowExecution(workflowId): Cancel running execution
 * - getExecutionMetrics(workflowId): Runtime analytics per execution
 *
 * Integration with workflows.service.ts:
 * - Create workflow_runs table: { id, workflow_id, user_id, status, started_at, ended_at, error }
 * - Create workflow_execution_logs table: { id, run_id, node_id, step_index, status, input, output, error, created_at }
 * - Create method: startWorkflowRun(workflowId) -> returns run_id
 * - Create method: logExecutionStep(runId, nodeId, stepIndex, status, input, output, error)
 * - Create method: updateWorkflowRunStatus(runId, status, error?) -> updates run record
 * - Create method: fetchWorkflowRuns(workflowId, limit?) -> returns execution history
 * - Create method: subscribeToWorkflowRun(runId) -> real-time updates via Supabase realtimelistener
 *
 * PART 10: ADVANCED FEATURES TODOs
 * ================================
 *
 * New methods:
 * - duplicateWorkflow(sourceId): Clone workflow with new definition
 * - createWorkflowVersion(workflowId): Save current state as version
 * - rollbackToVersion(workflowId, versionNumber): Restore from version history
 * - publishWorkflow(workflowId): Mark as published (separate from is_published boolean)
 * - shareWorkflow(workflowId, userId, permission): Grant user access to workflow
 * - getWorkflowAnalytics(workflowId): Stats - total runs, success rate, avg time, last run
 * - createTemplateFromWorkflow(workflowId, templateName, description): Save as reusable template
 * - fetchWorkflowTemplates(): List all available templates by category
 * - saveCustomAction(definition): Add custom action type to library
 * - saveCustomTrigger(definition): Add custom trigger type to library
 * - updateWorkflowAnalytics(workflowId, metrics): Periodic aggregation of run statistics
 *
 * Database schema extensions needed:
 * - workflow_versions: { id, workflow_id, version_num, definition, created_at, published_at }
 * - workflow_templates: { id, name, description, category, definition, created_by, created_at }
 * - workflow_shares: { id, workflow_id, shared_with_user_id, permission, created_at }
 * - workflow_analytics: { id, workflow_id, total_runs, success_count, failure_count, avg_duration, last_run_at }
 * - workflow_custom_actions: { id, user_id, name, definition, is_published, created_at }
 * - workflow_custom_triggers: { id, user_id, name, definition, is_published, created_at }
 */
import { supabase } from '@/lib/supabase-client';
// ============================================================================
// WORKFLOW CRUD OPERATIONS
// ============================================================================
/**
 * Fetch all workflows for the current user
 */
export async function fetchWorkflows() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user)
        throw new Error('User not authenticated');
    const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
    if (error)
        throw error;
    return data || [];
}
/**
 * Fetch a single workflow by ID
 */
export async function fetchWorkflow(workflowId) {
    const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('id', workflowId)
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Create a new workflow
 */
export async function createWorkflow(name, description) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user)
        throw new Error('User not authenticated');
    const defaultDefinition = {
        version: '1.0',
        trigger: {
            type: 'manual_trigger',
            configuration: { description: 'Manual workflow trigger' },
        },
        actions: [],
    };
    const { data, error } = await supabase
        .from('workflows')
        .insert({
        user_id: user.id,
        name,
        description,
        definition: defaultDefinition,
        is_published: false,
        is_active: true,
    })
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Update a workflow
 */
export async function updateWorkflow(workflowId, updates) {
    const { data, error } = await supabase
        .from('workflows')
        .update({
        ...updates,
        updated_at: new Date().toISOString(),
    })
        .eq('id', workflowId)
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Update workflow definition (trigger + actions)
 */
export async function updateWorkflowDefinition(workflowId, definition) {
    return updateWorkflow(workflowId, { definition });
}
/**
 * Delete a workflow
 */
export async function deleteWorkflow(workflowId) {
    const { error } = await supabase
        .from('workflows')
        .delete()
        .eq('id', workflowId);
    if (error)
        throw error;
}
/**
 * Publish/unpublish a workflow
 */
export async function publishWorkflow(workflowId, isPublished) {
    return updateWorkflow(workflowId, { is_published: isPublished });
}
/**
 * Toggle workflow active status
 */
export async function toggleWorkflowActive(workflowId, isActive) {
    return updateWorkflow(workflowId, { is_active: isActive });
}
// ============================================================================
// WORKFLOW EXECUTION OPERATIONS
// ============================================================================
/**
 * Start a workflow run (execution)
 */
export async function startWorkflowRun(workflowId, triggerEvent) {
    const { data, error } = await supabase
        .from('workflow_runs')
        .insert({
        workflow_id: workflowId,
        trigger_event: triggerEvent,
        status: 'pending',
    })
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Update workflow run status
 */
export async function updateWorkflowRunStatus(runId, status, errorMessage) {
    const updates = {
        status,
        completed_at: status === 'success' || status === 'failed' ? new Date().toISOString() : null,
    };
    if (errorMessage) {
        updates.error_message = errorMessage;
    }
    const { data, error } = await supabase
        .from('workflow_runs')
        .update(updates)
        .eq('id', runId)
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Fetch workflow runs (execution history)
 */
export async function fetchWorkflowRuns(workflowId, limit = 50) {
    const { data, error } = await supabase
        .from('workflow_runs')
        .select('*')
        .eq('workflow_id', workflowId)
        .order('created_at', { ascending: false })
        .limit(limit);
    if (error)
        throw error;
    return data || [];
}
/**
 * Fetch a single workflow run
 */
export async function fetchWorkflowRun(runId) {
    const { data, error } = await supabase
        .from('workflow_runs')
        .select('*')
        .eq('id', runId)
        .single();
    if (error)
        throw error;
    return data;
}
// ============================================================================
// WORKFLOW EXECUTION LOG OPERATIONS
// ============================================================================
/**
 * Log an execution step
 */
export async function logExecutionStep(runId, stepId, stepType, inputData, status, outputData, errorMessage) {
    const { data, error } = await supabase
        .from('workflow_execution_logs')
        .insert({
        run_id: runId,
        step_id: stepId,
        step_type: stepType,
        input_data: inputData,
        output_data: outputData || null,
        status,
        error_message: errorMessage || null,
    })
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Update an execution log entry
 */
export async function updateExecutionLog(logId, updates) {
    const { data, error } = await supabase
        .from('workflow_execution_logs')
        .update(updates)
        .eq('id', logId)
        .select()
        .single();
    if (error)
        throw error;
    return data;
}
/**
 * Fetch execution logs for a run
 */
export async function fetchExecutionLogs(runId) {
    const { data, error } = await supabase
        .from('workflow_execution_logs')
        .select('*')
        .eq('run_id', runId)
        .order('executed_at', { ascending: true });
    if (error)
        throw error;
    return data || [];
}
// ============================================================================
// WORKFLOW DATA EXTRACTION (For trigger data mapping)
// ============================================================================
/**
 * Extract data from a board item for use in workflow actions
 */
export async function extractBoardItemData(itemId) {
    const { data, error } = await supabase
        .from('board_items')
        .select('*')
        .eq('id', itemId)
        .single();
    if (error)
        throw error;
    return data || {};
}
/**
 * Extract data from a step/task
 */
export async function extractStepData(stepId) {
    const { data, error } = await supabase
        .from('steps')
        .select('*')
        .eq('id', stepId)
        .single();
    if (error)
        throw error;
    return data || {};
}
/**
 * Extract data from a mission/task
 */
export async function extractMissionData(missionId) {
    const { data, error } = await supabase
        .from('missions')
        .select('*')
        .eq('id', missionId)
        .single();
    if (error)
        throw error;
    return data || {};
}
/**
 * Extract data from a calendar event
 */
export async function extractCalendarEventData(eventId) {
    const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('id', eventId)
        .single();
    if (error)
        throw error;
    return data || {};
}
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
/**
 * Map trigger data using a mapping configuration
 * Example: { title: 'name', description: 'context' } maps trigger.name to title
 */
export function mapTriggerDataToAction(triggerData, mapping) {
    if (!mapping)
        return triggerData;
    const mapped = {};
    Object.entries(mapping).forEach(([actionField, triggerField]) => {
        if (triggerField in triggerData) {
            mapped[actionField] = triggerData[triggerField];
        }
    });
    return mapped;
}
/**
 * Replace template variables in text (e.g., {{id}}, {{title}})
 */
export function replaceTemplateVariables(text, data) {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        return data[key]?.toString() || match;
    });
}
/**
 * Validate workflow definition structure
 */
export function validateWorkflowDefinition(definition) {
    if (!definition.version || definition.version !== '1.0')
        return false;
    if (!definition.trigger || !definition.trigger.type)
        return false;
    if (!Array.isArray(definition.actions))
        return false;
    return true;
}
/**
 * Get workflow statistics
 */
export async function getWorkflowStats(workflowId) {
    const runs = await fetchWorkflowRuns(workflowId, 1000);
    const stats = {
        totalRuns: runs.length,
        successfulRuns: runs.filter((r) => r.status === 'success').length,
        failedRuns: runs.filter((r) => r.status === 'failed').length,
        lastRun: runs[0] || null,
    };
    return stats;
}
/**
 * Subscribe to real-time workflow updates
 */
export function subscribeToWorkflowUpdates(workflowId, callback) {
    return supabase
        .channel(`workflow:${workflowId}`)
        .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'workflows',
        filter: `id=eq.${workflowId}`,
    }, callback)
        .subscribe();
}
/**
 * Subscribe to workflow run updates
 */
export function subscribeToWorkflowRunUpdates(runId, callback) {
    return supabase
        .channel(`workflow_run:${runId}`)
        .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'workflow_runs',
        filter: `id=eq.${runId}`,
    }, callback)
        .subscribe();
}
